import { adjustColorBrightness } from "@/helpers/color";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import API from "@/helpers/API";
import { isTokenValid } from "@/helpers/token";
import { history } from "@/routers/AppRouter";

type TPharmacy = {
  color1: string;
  color2: string;
  description: string;
  hideAge: boolean;
  image: string;
  logo: string;
  subtitle: string;
  title: string;
};

type TPharmacyContext = {
  pharmacy: TPharmacy | null;
  setPharmacy: (pharmacy: TPharmacy) => void;
};

export const PharmacyContext = createContext<TPharmacyContext>({
  pharmacy: null,
  setPharmacy: () => {},
});

PharmacyContext.displayName = "Pharmacy";

const PHARMACY_STORAGE_KEY = "__pharmacy";

/**
 * Récupère la pharmacie depuis localStorage
 */
const getPharmacyFromStorage = (): TPharmacy | null => {
  try {
    const stored = localStorage.getItem(PHARMACY_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as TPharmacy;
    }
  } catch (error) {
    console.error(
      "Erreur lors de la récupération de la pharmacie depuis localStorage:",
      error,
    );
  }
  return null;
};

/**
 * Sauvegarde la pharmacie dans localStorage
 */
const savePharmacyToStorage = (pharmacy: TPharmacy | null): void => {
  try {
    if (pharmacy) {
      localStorage.setItem(PHARMACY_STORAGE_KEY, JSON.stringify(pharmacy));
    } else {
      localStorage.removeItem(PHARMACY_STORAGE_KEY);
    }
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde de la pharmacie dans localStorage:",
      error,
    );
  }
};

/**
 * Nettoie la pharmacie du localStorage (utilisé lors de la déconnexion)
 */
export const clearPharmacyStorage = (): void => {
  savePharmacyToStorage(null);
};

export const PharmacyProvider: FC<PropsWithChildren> = ({ children }) => {
  // Restaure la pharmacie depuis localStorage au chargement initial
  const [pharmacy, setPharmacyState] = useState<TPharmacy | null>(() =>
    getPharmacyFromStorage(),
  );
  const pharmacyRef = useRef(pharmacy);

  // Met à jour la ref quand pharmacy change
  useEffect(() => {
    pharmacyRef.current = pharmacy;
  }, [pharmacy]);

  const setPharmacy = (newPharmacy: TPharmacy) => {
    setPharmacyState(newPharmacy);
    savePharmacyToStorage(newPharmacy);
  };

  // Vérifie la validité du token au chargement et nettoie si invalide
  useEffect(() => {
    const storedPharmacy = getPharmacyFromStorage();
    if (storedPharmacy && !isTokenValid()) {
      // Token invalide, nettoyer la pharmacie et rediriger vers login
      setPharmacyState(null);
      clearPharmacyStorage();
      API.forgetAuth!();
      history.push("/login");
    }
  }, []); // Seulement au montage

  // Écoute les changements de route pour vérifier la validité du token
  useEffect(() => {
    const unlisten = history.listen(() => {
      const token = API.getTokenFromCookie!();

      if (!token) {
        // Pas de token, nettoyer la pharmacie
        if (pharmacyRef.current) {
          setPharmacyState(null);
          clearPharmacyStorage();
        }
      } else if (!isTokenValid()) {
        // Token invalide, nettoyer et déconnecter
        setPharmacyState(null);
        clearPharmacyStorage();
        API.forgetAuth!();
        history.push("/login");
      }
    });

    return () => {
      unlisten();
    };
  }, []); // Le listener est créé une seule fois

  // Applique les styles CSS de la pharmacie
  useEffect(() => {
    if (pharmacy && pharmacy?.color1 !== "" && pharmacy?.color2 !== "") {
      document.documentElement.style.setProperty(
        "--primary-color",
        pharmacy.color1,
      );
      document.documentElement.style.setProperty(
        "--primary-color-dark",
        adjustColorBrightness(pharmacy.color1, -10),
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        pharmacy.color2,
      );
    }
  }, [pharmacy]);

  return (
    <PharmacyContext.Provider
      value={{
        pharmacy,
        setPharmacy,
      }}
    >
      {children}
    </PharmacyContext.Provider>
  );
};

export const usePharmacy = () => useContext(PharmacyContext);
